import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useAtom } from "jotai";
import MGAAPI from "../../../api/index";
import {
  Button,
  Divider,
  FileInput,
  Grid,
  Group,
  Select,
  SegmentedControl,
  Text,
  TextInput,
  useMantineColorScheme,
  Switch,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import {
  IconCalendar,
  IconCaretDownFilled,
  IconFileSearch,
  IconPercentage,
} from "@tabler/icons-react";
import { Trans, useTranslation } from "react-i18next";
import { AccordionItem } from "../../AccordianItem";
import TimeSeriesGraph from "../../timeSeriesGraph";
import { addedStepsAtom } from "../../../utils/atoms/addedStepsAtom";
import displayError from "../../../constants/utils";
import fileDownload from "js-file-download";
import "./index.scss";
import { helpAtom } from "../../../utils/atoms/helpAtom";
import pvJson from "../../../components/help/helpCardMenu.json";
import PVInvestment from "../PVInvestment";
import { projectDetailsAtom } from "../../../utils/atoms/projectDetailsAtom";
import { generateTimeSeries, useProjectDateFormat } from "../../../utils/helpers";
import ProjectNumberInput from "../../common/ProjectNumberInput";
import { responsiveCol, useCurrentDesignComponentLists } from "../../../constants/helper";
import { saveDisabledAtom } from "../../../utils/atoms/saveDisabledAtom";
import { getErrorMessage } from "../../../utils/errorUtils";
import { computeNominalAcPower } from "../PVValidation";
import { helpContextMenuAtom } from "../../../utils/atoms/helpContextMenuAtom";

const MGA_API = new MGAAPI();

const PVConfigure = ({
  form,
  updatePVData,
  componentDetails,
  isLoading,
  setIsLoading,
}) => {
  const { t } = useTranslation();
  const pvParameters = componentDetails?.pv?.componentAttributes?.pvParameters;
  const { colorScheme } = useMantineColorScheme();
  const grayIconStyle = useMemo(
    () => ({
      padding: "4px",
    }),
    [colorScheme],
  );
  const [dataAsssetElectric, setDataAssestElectric] = useState(
    pvParameters?.profileFile || []
  );
    const [saveDisabled, setSaveDisabled] = useAtom(saveDisabledAtom);
  
  const [fileContent, setFileContent] = useState({});

  const timeInputRef = useRef();
  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  const [projectDetails] = useAtom(projectDetailsAtom);
  const projectDateFormat = useProjectDateFormat();
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(
    projectDetails?.designComponentsList
  );
  const [manualCreationData, setManualCreationData] = useState(
    pvParameters?.createProfileData || []
  );
  const [isUploadProfile, setIsUploadProfile] = useState(false);
  const containerRef = useRef(null);
  const graphRef = useRef();
  const createProfileButtonRef = useRef(null);

  const [helpMenuData] = useAtom(helpContextMenuAtom);

  const getFieldAttributes = (fieldKey) => {
    const field = helpMenuData?.pv?.[fieldKey];
    return {
      title: field?.title || "",
      "data-description": field?.description || "",
      // placeholder: field?.placeholder || "",
    };
  };

  const projectId = projectDetails?.projectDTO?.projectId;

  const degradationFactorValue = form?.values?.componentAttributes?.pvParameters?.degradationFactor;
  const lifetimeYearsValue = form?.values?.economicAttributes?.pvParameters?.pvLifetime;
  const degradationNote = useMemo(() => {
    if (
      degradationFactorValue === null ||
      degradationFactorValue === undefined ||
      String(degradationFactorValue).trim() === "" ||
      lifetimeYearsValue === null ||
      lifetimeYearsValue === undefined ||
      String(lifetimeYearsValue).trim() === ""
    ) {
      return null;
    }

    const yearlyPercentage = Number(degradationFactorValue);
    const lifetime = Number(lifetimeYearsValue);
    if (!Number.isFinite(yearlyPercentage) || !Number.isFinite(lifetime) || lifetime <= 0) {
      return null;
    }

    const averageReduction = (yearlyPercentage / 100) * ((lifetime + 1) / 2) * 100;
    const reductionText = Number.isInteger(averageReduction)
      ? String(averageReduction)
      : averageReduction.toFixed(1).replace(/\.0$/, "");

    return {
      dfText: yearlyPercentage.toFixed(1),
      reductionText,
    };
  }, [degradationFactorValue, lifetimeYearsValue]);

  const newProfileObject = {
    relDCPower: pvParameters?.relDCPower,
    invEfficiency: pvParameters?.invEfficiency,
    relACPower: pvParameters?.relACPower,
    tilt: pvParameters?.tilt,
    orientation: pvParameters?.orientation,
    systemLoss: pvParameters?.systemLoss,
    axisType: pvParameters?.axisType,
  };
    
   useEffect(() => {
     const containerElement = containerRef.current;
     if (!containerElement) return;
 
     containerElement.addEventListener("focusin", handleFocus);
     containerElement.addEventListener("focusout", handleBlur);
 
     return () => {
       containerElement.removeEventListener("focusin", handleFocus);
       containerElement.removeEventListener("focusout", handleBlur);
     };
   }, []);
   
  const handleContainerClick = (event) => {
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
    if (interactiveTags.includes(event.target.tagName)) return;
    const cardElement = document.querySelector('[card-id="accordion-pv-2"]');
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      cardElement.style.transition = "background-color 0.5s ease";
      cardElement.style.border = "2px solid #42b4e6";
      setTimeout(() => {
        cardElement.style.border = "";
      }, 2000);
    }
  };

  const handleContainerKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContainerClick(event);
    }
  };
  const handleBlur = (event) => {
    if (containerRef.current && !containerRef.current.contains(event.target)) {
      setHelperAtom({
        ...helperAtom,
        isFocused: false,
        helpSection: {
          title: "",
          description: "",
          type: "",
          other: "",
        },
      });
    }
  };
  const handleFocus = (event) => {
    const dataId = event.target.getAttribute("data-id");
    if (dataId) {
      const cardElement = document.querySelector(`[card-id="${dataId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
        cardElement.style.transition = "background-color 0.5s ease";
        cardElement.style.border = "2px solid #42b4e6";
        setTimeout(() => {
          cardElement.style.border = "";
        }, 2000);
      }
    }
  };

  const handleGetProfileClick = async () => {
    if (!fileContent) {
      return;
    }
    setIsLoading(true);
    const payload = {
      startDate: pvParameters?.profileDetail?.startDate,
      startTime: pvParameters?.profileDetail?.startTime,
      units: pvParameters?.profileDetail?.units,
      dataSource: "Upload",
      componentType: "PV",
      timeResolution: pvParameters?.profileDetail?.timeResolution || 15,
    };
    const formData = new FormData();
    formData.append("file", pvParameters.file);
    formData.append(
      "profileDetail",
      new Blob([JSON.stringify(payload)], { type: "application/json" })
    );
    setSaveDisabled(true);
    try {
      const result = await MGA_API.getProfile(formData).then((res) => {
        setDataAssestElectric(res);
        setManualCreationData([]);
        updatePVData("componentAttributes", "profileFile", res);
        updatePVData("componentAttributes", "file", null);
       const units = pvParameters?.profileDetail?.units;
      let updatedUploadNominalAcPower = computeNominalAcPower(res);
      if (units && units?.toLowerCase() === "w") {
        updatedUploadNominalAcPower = updatedUploadNominalAcPower / 1000;
      }
      updatePVData("componentAttributes", "powNomRef", updatedUploadNominalAcPower?.toFixed(2));
      });
      setIsLoading(false);
      setSaveDisabled(false)
      displayError("success", t("pv.file.upload.success"));
    } catch (error) {
      setIsLoading(false);
      setSaveDisabled(false);
      // Clear file on error so user can retry uploading the same file
      setFileContent({});
      updatePVData("componentAttributes", "file", null);
      updatePVData("componentAttributes", "isUploadPvFileButtonClicked", false);
      displayError("error", error?.response?.data?.message || t("pv.file.upload.error"));
    }
  };

  const handleFileInputChange = (file) => {
    if (!file) return;
    setFileContent(file);
    updatePVData("componentAttributes", "file", file);
    updatePVData("componentAttributes", "profileFile", []);
  };

  const header = () => (
    <>
      <span className="stepText col-md-1 px-0">
        {" "}
        {`Step ${addedStepsSeries.indexOf("pv") + 1}: ${
          componentDetails?.pv?.uiAttributes?.pvParameters?.pvSetup ===
          "NewAndExisting"
            ? "C"
            : "B"
        }.`}
      </span>
      <span
        className={`ml-2 col-md-8 px-0 ${
          colorScheme === "dark" ? "se-white" : "se-accordian-title"
        }`}
      >

        {" "}
        {t("pv.configure.uploadOrCreateProfile")}
        {/* Upload Your Power Profile To Configure your
        <b style={{ fontWeight: "800" }}>
          {" "}
          {componentDetails?.pv?.uiAttributes?.pvParameters?.pvSetup?.toLocaleLowerCase() ===
          "new"
            ? "New"
            : "Existing"}
        </b>{" "}
        PV System */}
      </span>
    </>
  );

   const header1 = () => (
    <>
   
      <span className="stepText col-md-1 px-0">
        {" "}
        Step {addedStepsSeries.indexOf("pv") + 1}: D.
      </span>
       &nbsp;<span
            className={`ml-2 col-md-8 px-0 fs-6 ${
              colorScheme === "dark" ? "se-white" : "se-accordian-title"
            }`}
          >
            {t("PVInvestment")}
          </span>
    </>
  );  

  const handleFileDownload = (type) => {
    MGA_API.downloadImportTemplate(type)
      .then(({ data, fileName }) => {
        fileDownload(data, fileName || "PV");
        displayError("success", t("common.file.download.success"));
      })
      .catch((error) => {
        console.log("Error Downloading File:", error);
        displayError("error", t("common.file.download.error"));
      });
  };

  const currentDate = new Date();
  const reducedDate = new Date(currentDate);

  reducedDate.setDate(currentDate.getDate() - 1); // Subtract one day
  reducedDate.setFullYear(currentDate.getFullYear() - 1); // Subtract one year

  const fetchPvProfile = () => {
    setIsLoading(true);
    setSaveDisabled(true);
    MGA_API.getPvProfile(projectId, newProfileObject)
      .then((response) => {
        setManualCreationData(response);
        updatePVData("componentAttributes", "createProfileData", response);
        const updatedNominalAcPower = computeNominalAcPower(response);
        updatePVData("componentAttributes", "powNomRef", updatedNominalAcPower?.toFixed(2));
        setIsLoading(false);
        setSaveDisabled(false);
      })
      .catch((error) => {
        setSaveDisabled(false);
        setManualCreationData([]);
        updatePVData("componentAttributes", "createProfileData", []);
        setIsLoading(false);
        console.error("Error fetching weather data", error);
        displayError("error", getErrorMessage(error) || t("pv.profile.creation.error"));
      });
  };

  const isAllFieldsFilled = (data) => {
    return data && typeof data === "object"
      ? Object.values(data).every(
          (value) => value !== null && value !== undefined && value !== ""
        )
      : false;
  };

  const isCreateProfileDisabled = () => {
    const axis = pvParameters?.axisType;
    switch (axis) {
      case "VERTICAL_TRACKING":
        return !isAllFieldsFilled({
          relDCPower: pvParameters?.relDCPower,
          invEfficiency: pvParameters?.invEfficiency,
          relACPower: pvParameters?.relACPower,
          orientation: pvParameters?.orientation,
          systemLoss: pvParameters?.systemLoss,
          axisType:pvParameters?.axisType
        });
      case "HORIZONTAL_TRACKING":
        return !isAllFieldsFilled({
          relDCPower: pvParameters?.relDCPower,
          invEfficiency: pvParameters?.invEfficiency,
          relACPower: pvParameters?.relACPower,
          tilt: pvParameters?.tilt,
          systemLoss: pvParameters?.systemLoss,
          axisType:pvParameters?.axisType
        });
      case "DUAL_AXIS_TRACKING":
        return !isAllFieldsFilled({
          relDCPower: pvParameters?.relDCPower,
          invEfficiency: pvParameters?.invEfficiency,
          relACPower: pvParameters?.relACPower,
          systemLoss: pvParameters?.systemLoss,
          axisType:pvParameters?.axisType
        });
      default:
        return false;
    }
  };

  useEffect(() => {
    if (
      !componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV &&
      pvParameters?.createProfileData?.length
    ) {
      setManualCreationData(pvParameters?.createProfileData);
    } else {
      setManualCreationData([]);
    }
  }, [pvParameters?.createProfileData]);

  // Scroll into view for Create Profile button when its error is present
  useEffect(() => {
    const err =
      form.errors[
        "componentAttributes.pvParameters.createProfileButtonClicked"
      ];
    if (err && createProfileButtonRef?.current) {
      try {
        createProfileButtonRef.current.scrollIntoView({
          behavior: "smooth",
          block: "bottom",
        });
        // Focus after a tiny delay to ensure element is in view
        setTimeout(() => {
          // Mantine Button is focusable; call focus safely
          if (createProfileButtonRef.current?.focus) {
            createProfileButtonRef.current.focus();
          }
        }, 200);
      } catch (e) {
        // no-op
      }
    }
  }, [
    form.errors[
      "componentAttributes.pvParameters.createProfileButtonClicked"
    ],
  ]);

  useEffect(() => {
    if (
      componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV &&
      pvParameters?.profileFile?.length
    ) {
      setDataAssestElectric(pvParameters?.profileFile);
    } else {
      setDataAssestElectric([]);
    }
  }, [pvParameters?.profileFile]);

  useEffect(() => {
    if (componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV) {
      setIsUploadProfile(true);
    } else {
      setIsUploadProfile(false);
    }
  }, [componentDetails?.pv]);

  const getClassNameCreateProfileButton = () => {
    const pvFieldKeys = ["tilt", "orientation"];
    const errorFields = pvFieldKeys?.filter(
      (key) => form.errors[`componentAttributes.pvParameters.${key}`]
    );
    return errorFields?.length > 0 ? "get-profile-error" : "get-profile-button";
  };

  const validateFieldsForGetProfile = (paths = []) => {
    const invalid = [];
    paths.forEach((p) => {
      const error = form.validateField(p);
      if (error?.hasError) invalid.push(p);
    });
    if (invalid.length > 0) {
      const firstErrorPath = invalid[0];
      const inputNode = form.getInputNode(firstErrorPath);
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: "smooth", block: "center" });
        inputNode.focus();
      }
      return { hasErrors: true, invalidPaths: invalid };
    }
    return { hasErrors: false, invalidPaths: [] };
  };

  const children = () => (
    <div
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
      data-id="accordion-pv-2"
      role="button"
    >
      <Group position="center" align="center" className="mt-2 pb-4">
        <SegmentedControl
          data={[
            { label: t("pv.createProfile"), value: "create" },
            { label: t("pv.uploadProfile"), value: "upload" },
          ]}
          value={isUploadProfile ? "upload" : "create"}
          onChange={(val) => {
            const upload = val === "upload";
            setIsUploadProfile(upload);
            updatePVData("uiAttributes", "isUploadPV", upload);
          }}
          color="seGreen.0"
          size="md"
        />
      </Group>
      {/* <Group position="center" align="center" className="mt-2 pb-4">
        <Text>Create Profile</Text>
        <Switch
          checked={componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV}
          color="seGreen.0"
          {...form.getInputProps("uiAttributes.pvParameters.isUploadPV")}
          onChange={(e) => {
            updatePVData("uiAttributes", "isUploadPV", e.currentTarget.checked);
          }}
          size="md"
        />
      </Group> */}
      {componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV ? (
        <>
          <Grid gutter="md" className="mt-2 mb-2">
            <Grid.Col {...responsiveCol}>
              <DateInput
                key={`pv-start-date-${projectDateFormat}`}
                {...getFieldAttributes("field-1")}
                data-id="field-1"
                label={t("startDate")}
                value={new Date(pvParameters?.profileDetail?.startDate)}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.profileDetail.startDate"
                )}
                onChange={(date) => {
                  updatePVData(
                    "componentAttributes",
                    "profileDetail.startDate",
                    date
                  );
                }}
                // placeholder={t("pv.placeholder.dateInput")}
                valueFormat={projectDateFormat}
                required
                rightSection={
                  <div style={grayIconStyle}>
                    <IconCalendar size="1.1rem" stroke={1.5} />
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                popoverProps={{
                  withinPortal: true,
                  position: "bottom-start",
                }}
                maxDate={reducedDate}
                clearable
              />
            </Grid.Col>
            <Grid.Col {...responsiveCol}>
              <Select
                {...getFieldAttributes("field-2")}
                data-id="field-2"
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("startTime")}
                data={generateTimeSeries()}
                value={pvParameters?.profileDetail?.startTime}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.profileDetail.startTime"
                )}
                onChange={(value) => {
                  updatePVData(
                    "componentAttributes",
                    "profileDetail.startTime",
                    value
                  );
                }}
                required
                ref={timeInputRef}
                size="sm"
                withinPortal={true}
                styles={{
                  dropdown: {
                    left: 0,
                    width: "100px",
                    maxWidth: "100px",
                  },
                }}
                searchable
              />
            </Grid.Col>
            <Grid.Col {...responsiveCol}>
              <Select
                {...getFieldAttributes("field-3")}
                data-id="field-3"
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("timeResolution")}
                data={[
                  { value: 15, label: "15" },
                  { value: 30, label: "30" },
                  { value: 60, label: "60" },
                ]}
                value={pvParameters?.profileDetail?.timeResolution}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.profileDetail.timeResolution"
                )}
                onChange={(value) => {
                  updatePVData(
                    "componentAttributes",
                    "profileDetail.timeResolution",
                    value
                  );
                }}
                withinPortal
              />
            </Grid.Col>
            <Grid.Col {...responsiveCol}>
              <Select
                {...getFieldAttributes("field-4")}
                data-id="field-4"
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("pv.units")}
                data={[
                  { value: "kW", label: "kW" },
                  { value: "W", label: "W" },
                  { value: "kWh", label: "kWh", disabled: true },
                  { value: "Wh", label: "Wh", disabled: true },
                ]}
                value={pvParameters?.profileDetail?.units}
                required
                {...form.getInputProps(
                  "componentAttributes.pvParameters.profileDetail.units"
                )}
                onChange={(value) => {
                  updatePVData(
                    "componentAttributes",
                    "profileDetail.units",
                    value
                  );
                }}
                withinPortal
              />
            </Grid.Col>
          </Grid>
          <Grid gutter="md" className="mt-2 mb-2">
            <Grid.Col className="get-profile" {...responsiveCol}>
              <FileInput
                {...getFieldAttributes("field-5")}
                data-id="field-5"
                // label={t("uploadLoadFile")}
                label={
                  <>
                    <div>
                      {t("uploadPvFile")}
                      <span style={{ color: "#fa5252" }}> *</span>
                    </div>
                    <span
                      style={{
                        textDecoration: "underline",
                        textUnderlineOffset: "3px",
                        color: "#42b4e6",
                        cursor: "pointer",
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleFileDownload("pv");
                        }
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleFileDownload("pv");
                      }}
                    >
                      {t("downloadTemplate")}
                    </span>
                  </>
                }
                // placeholder={t("pv.placeholder.uploadFile")}
                accept=".xls,.xlsx,.csv"
                {...form.getInputProps("componentAttributes.pvParameters.file")}
                onChange={(file) => {
                  handleFileInputChange(file);
                  updatePVData(
                    "componentAttributes",
                    "isUploadPvFileButtonClicked",
                    !!file
                  );
                }}
                rightSection={
                  <div style={grayIconStyle}>
                    <IconFileSearch size={18} />
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
              />
            </Grid.Col>
            <Grid.Col
              {...responsiveCol}
              className={
                form.errors["componentAttributes.pvParameters.file"]
                  ? "get-profile-error"
                  : "get-profile-button"
              }
            >
              <Button
                variant="filled"
                onClick={() => {
                  handleGetProfileClick();
                  updatePVData(
                    "componentAttributes",
                    "isUploadPvFileButtonClicked",
                    false
                  );
                }}
                sx={{
                  backgroundColor: "#626469",
                  "&:hover": {
                    backgroundColor: "#494b50",
                  },
                }}
                disabled={!pvParameters?.isUploadPvFileButtonClicked}
              >
                {t("getProfile")}
              </Button>
            </Grid.Col>
          </Grid>
          <Grid gutter="md" className="mb-2">
            <Grid.Col
              gutter="md"
              className="mt-2 mb-2"
              id="timeSeries"
              style={{
                position: "relative",
                maxWidth: "100% !important",
                boxSizing: "border-box",
              }}
            >
              <div id="scroll-into-graph" ref={graphRef}>
                {isLoading && <se-loading loading={isLoading} />}
                {dataAsssetElectric?.length > 0 && (
                  <TimeSeriesGraph
                    dataAssset={dataAsssetElectric}
                    color={"#36a2eb"}
                    name="Solar PV"
                    yAxisName={pvParameters?.profileDetail?.units}
                    chartStartDate={pvParameters?.profileDetail?.startDate}
                    fullViewport={false}
                    embedHeight={500}
                    showMinimap={true}
                    minimapGap={40}
                    startTime={pvParameters?.profileDetail?.startTime}
                    interval={pvParameters?.profileDetail?.timeResolution}
                    legendLabels={{ electricalLoad: "PV Power" }}
                    tooltipLabels={{ generic: "PV Power" }}
                  />
                )}
              </div>
            </Grid.Col>
          </Grid>
        </>
      ) : (
        <>
          <Grid gutter="md" className="mt-2 mb-2">
             <Grid.Col {...responsiveCol}>
              <Select
                {...getFieldAttributes("field-2")}
                data-id="field-2"
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("pv.label.axisType")}
                data={[{ value: "NO_TRACKING", label: t("pv.axisType.noTracking") }, { value: "VERTICAL_TRACKING", label: t("pv.axisType.verticalTracking") }, { value: "HORIZONTAL_TRACKING", label: t("pv.axisType.horizontalTracking") }, { value: "DUAL_AXIS_TRACKING", label: t("pv.axisType.dualAxisTracking") }]}
                value={pvParameters?.axisType}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.axisType"
                )}
                onChange={(value) => {
                  updatePVData(
                    "componentAttributes",
                    "axisType",
                    value
                  );
                }}
                required
                size="sm"
                withinPortal={true}
                searchable
              />
            </Grid.Col>
            <Grid.Col {...responsiveCol}>
              <ProjectNumberInput
                {...getFieldAttributes("field-6")}
                data-id="field-6"
                label={t("pv.label.ratedDcPower")}
                required
                type="text"
                // placeholder={t("pv.placeholder.ratedDcPower")}
                value={pvParameters?.relDCPower || ""}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.relDCPower"
                )}
                onChange={(e) =>
                  updatePVData(
                    "componentAttributes",
                    "relDCPower",
                    e.target.value
                  )
                }
                rightSection={<div style={grayIconStyle}>kW</div>}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
              />
            </Grid.Col>
            <Grid.Col {...responsiveCol}>
              <ProjectNumberInput
                {...getFieldAttributes("field-7")}
                data-id="field-7"
                label={t("pv.label.inverterEfficiency")}
                required
                type="text"
                // placeholder={t("pv.placeholder.inverterEfficiency")}
                value={pvParameters?.invEfficiency || ""}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.invEfficiency"
                )}
                onChange={(e) =>
                  updatePVData(
                    "componentAttributes",
                    "invEfficiency",
                    e.target.value
                  )
                }
                rightSection={<div style={grayIconStyle}>%</div>}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
              />
            </Grid.Col>
            <Grid.Col {...responsiveCol}>
              <ProjectNumberInput
                {...getFieldAttributes("field-8")}
                data-id="field-8"
                label={t("pv.label.ratedAcPower")}
                required
                type="text"
                // placeholder={t("pv.placeholder.ratedAcPower")}
                value={pvParameters?.relACPower || ""}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.relACPower"
                )}
                onChange={(e) =>
                  updatePVData(
                    "componentAttributes",
                    "relACPower",
                    e.target.value
                  )
                }
                rightSection={<div style={{ ...grayIconStyle, opacity: 0.5 }}>kW</div>}
                // rightSectionProps={{ style: { pointerEvents: "none" } }}
                // disabled={true}
                styles={{ input: { pointerEvents: "none", opacity: 0.5 } }}
              />
            </Grid.Col>
            <Grid.Col {...responsiveCol}>
              <ProjectNumberInput
                {...getFieldAttributes("field-9")}
                data-id="field-9"
                label={t("pv.label.systemLoss")}
                required
                type="text"
                // placeholder={t("pv.placeholder.systemLoss")}
                value={pvParameters?.systemLoss || ""}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.systemLoss"
                )}
                onChange={(e) =>
                  updatePVData(
                    "componentAttributes",
                    "systemLoss",
                    e.target.value
                  )
                }
                rightSection={<div style={grayIconStyle}>%</div>}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
              />
            </Grid.Col>
           {(pvParameters?.axisType === "VERTICAL_TRACKING"||pvParameters?.axisType === "DUAL_AXIS_TRACKING") ? null : <Grid.Col {...responsiveCol}>
              <ProjectNumberInput
                {...getFieldAttributes("field-10")}
                data-id="field-10"
                label={
                  <span>
                    {t("pv.label.tiltAngle")}
                    <span style={{ fontSize: '11px', color: '#888', marginTop: 2 }}>
                      {t("pv.hint.tiltAngle")}
                    </span>
                  </span>
                }
                required
                type="text"
                // placeholder={t("pv.placeholder.tiltAngle")}
                value={pvParameters?.tilt || ""}
                {...form.getInputProps("componentAttributes.pvParameters.tilt")}
                onChange={(e) =>
                  updatePVData("componentAttributes", "tilt", e.target.value)
                }
                rightSection={
                  <div style={{ ...grayIconStyle, marginRight: "2rem" }}>
                    degrees
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
              />
            </Grid.Col>}
            {(pvParameters?.axisType === "HORIZONTAL_TRACKING"||pvParameters?.axisType === "DUAL_AXIS_TRACKING") ? null : (
              <Grid.Col {...responsiveCol}>
                <ProjectNumberInput
                  {...getFieldAttributes("field-11")}
                  data-id="field-11"
                  label={
                    <span>
                    {t("pv.label.orientation")}
                    <span style={{ fontSize: '11px', color: '#888', marginTop: 2 }}>
                      {t("pv.hint.orientation")}
                    </span>
                  </span>
                }
                // label="Orientation Direction (South: 0, North: 180, East: 270, West: 90 Degrees)"
                required
                type="text"
                // placeholder={t("pv.placeholder.orientation", { defaultValue: "Add orientation direction" })}
                value={pvParameters?.orientation || ""}
                {...form.getInputProps(
                  "componentAttributes.pvParameters.orientation"
                )}
                onChange={(e) =>
                  updatePVData(
                    "componentAttributes",
                    "orientation",
                    e.target.value
                  )
                }
                rightSection={
                  <div style={{ ...grayIconStyle, marginRight: "2rem" }}>
                    degrees
                  </div>
                }
                rightSectionProps={{ style: { pointerEvents: "none" } }}
              />
            </Grid.Col>)}
            <Grid.Col className={getClassNameCreateProfileButton()} {...responsiveCol}>
              <Button
                ref={createProfileButtonRef}
                variant="filled"
                onClick={() => {
                  const { hasErrors } = validateFieldsForGetProfile([
                    "componentAttributes.pvParameters.relDCPower",
                    "componentAttributes.pvParameters.invEfficiency",
                    "componentAttributes.pvParameters.systemLoss",
                    "componentAttributes.pvParameters.tilt",
                    "componentAttributes.pvParameters.orientation",
                    "componentAttributes.pvParameters.axisType"
                  ]);
                  if (hasErrors) return;
                  updatePVData(
                    "componentAttributes",
                    "createProfileButtonClicked",
                    true
                  );
                  fetchPvProfile();
                }}
                sx={{
                  backgroundColor: "#626469",
                  "&:hover": {
                    backgroundColor: "#494b50",
                  },
                }}
                disabled={isCreateProfileDisabled()}
              >
                {t("getProfile")}
              </Button>
              {form.errors[
                "componentAttributes.pvParameters.createProfileButtonClicked"
              ] && (
                <Text className="ms-2" color="red" size="sm" mt={4}>
                  {
                    form.errors[
                      "componentAttributes.pvParameters.createProfileButtonClicked"
                    ]
                  }
                </Text>
              )}
            </Grid.Col>
          </Grid>
          <Grid gutter="md" className="mb-2">
            <Grid.Col
              gutter="md"
              className="mt-2 mb-2"
              id="timeSeries"
              style={{
                position: "relative",
                maxWidth: "100% !important",
                boxSizing: "border-box",
              }}
            >
              {isLoading && <se-loading loading={isLoading} />}
              {manualCreationData?.length > 0 && (
                <TimeSeriesGraph
                  dataAssset={manualCreationData}
                  color={"#36a2eb"}
                  name="PV Create Profile"
                  yAxisName="kW"
                  embedHeight={500}
                  showMinimap={true}
                  minimapGap={40}
                  legendLabels={{ electricalLoad: "PV Power" }}
                  tooltipLabels={{ generic: "PV Power" }}
                />
              )}
            </Grid.Col>
          </Grid>
        </>
      )}
      {/* <Divider my="sm" /> */}
      
    </div>
  );
  
  const header_1 = () => (
    <>
   
      <span className="stepText col-md-1 px-0">
        {" "}
        Step {addedStepsSeries.indexOf("pv") + 1}: C.
      </span>
       &nbsp;<span
            className={`ml-2 col-md-8 px-0 fs-6 ${
              colorScheme === "dark" ? "se-white" : "se-accordian-title"
            }`}
          >
            {t("PVSolarParameters")}
          </span>
    </>
  );

  const children_1 = () => (
    <Grid gutter="md" className="mt-2 mb-2">
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          {...getFieldAttributes("field-12")}
          data-id="field-12"
          label={t("Nominal_AC_power")}
          required
          type="text"
          disabled={!componentDetails?.pv?.uiAttributes?.pvParameters?.isUploadPV}
          value={pvParameters?.powNomRef || ""}
          {...form.getInputProps("componentAttributes.pvParameters.powNomRef")}
          onChange={(e) =>
            updatePVData("componentAttributes", "powNomRef", e.target.value)
          }
          rightSection={<div style={grayIconStyle}>kW</div>}
          rightSectionProps={{ style: { pointerEvents: "none" } }}
        />
      </Grid.Col>
      <Grid.Col {...responsiveCol}>
        <ProjectNumberInput
          onClick={(e) => e.stopPropagation()}
          {...getFieldAttributes("field-19")}
          data-id="field-19"
          label={t("PV_Degradation_Factor")}
          required
          {...form.getInputProps(
            "componentAttributes.pvParameters.degradationFactor",
          )}
          onChange={(e) => {
            const trimmedValue = e.currentTarget.value
              .trim()
            updatePVData(
              "componentAttributes",
              "degradationFactor",
              trimmedValue,
            );
          }}
          rightSection={
            <div style={{ ...grayIconStyle }}>
              <IconPercentage size={18} />
            </div>
          }
          rightSectionProps={{ style: { pointerEvents: "none" } }}
        />
        {degradationNote && (
          <div style={{ marginTop: "6px", color: "#eb7857", fontSize: "12px", lineHeight: 1.35 }}>
            <Trans
              i18nKey="component.degradation.note"
              values={{ df: degradationNote.dfText, reduction: degradationNote.reductionText }}
              components={{ strong: <strong /> }}
            />
          </div>
        )}
      </Grid.Col>
    </Grid>
  );
  return (
    <div ref={containerRef}>
      <AccordionItem
        id={2}
        header={header()}
        key={2}
        children={children()}
        height="fit-content"
        isVisible={true}
      />
      <AccordionItem
        id={3}
        header={header_1()}
        key={3}
        children={children_1()}
      />
      <AccordionItem id={4} header={header1()} key={4}>
        <PVInvestment
          stepName="step3"
          form={form}
          updatePVData={updatePVData}
          componentDetails={componentDetails}
          getFieldAttributes={getFieldAttributes}
        />
      </AccordionItem>
    </div>
  );
};
export default PVConfigure;
